import { useTranslation } from 'react-i18next';
import { useParams, Link, Navigate } from 'react-router-dom';
import { ArrowLeft, Zap, Lightbulb, Network, Tv, DoorClosed } from 'lucide-react';
import { CallToAction } from '../components/CallToAction';

// Mappings
import installationImg from '../assets/images/services/installation.png';
import lightingImg from '../assets/images/services/lighting.png';
import networkImg from '../assets/images/services/network.png';
import entertainmentImg from '../assets/images/services/entertainment.png';
import intercomImg from '../assets/images/services/intercom.png';

const serviceData = {
    elektroinstallation: {
        key: 'installation',
        icon: Zap,
        image: installationImg,
    },
    beleuchtung: {
        key: 'lighting',
        icon: Lightbulb,
        image: lightingImg,
    },
    netzwerktechnik: {
        key: 'network',
        icon: Network,
        image: networkImg,
    },
    unterhaltungselektronik: {
        key: 'entertainment',
        icon: Tv,
        image: entertainmentImg,
    },
    sprechanlagen: {
        key: 'intercom',
        icon: DoorClosed,
        image: intercomImg,
    },
};

export const ServiceDetail = () => {
    const { t } = useTranslation();
    const { serviceId } = useParams<{ serviceId: string }>();

    if (!serviceId || !(serviceId in serviceData)) {
        return <Navigate to="/leistungen" replace />;
    }

    const { key, icon: Icon, image } = serviceData[serviceId as keyof typeof serviceData];

    return (
        <>
            <section className="service-detail-hero" id="top">
                <div className="container">
                    <Link to="/leistungen" className="back-link">
                        <ArrowLeft className="h-4 w-4" /> {t('nav.services')}
                    </Link>
                    <div className="service-detail-hero__inner">
                        <div className="service-detail-hero__content">
                            <span className="section__eyebrow">{t('servicesPage.cardLabel')}</span>
                            <h1>{t(`servicesPage.cards.${key}.title`)}</h1>
                            <p className="service-detail-hero__lead">{t(`servicesPage.cards.${key}.lead`)}</p>
                            <ul className="service-detail-hero__list">
                                <li>{t(`servicesPage.cards.${key}.points.one`)}</li>
                                <li>{t(`servicesPage.cards.${key}.points.two`)}</li>
                                <li>{t(`servicesPage.cards.${key}.points.three`)}</li>
                            </ul>
                            <div className="service-detail-hero__actions">
                                <Link className="btn btn--primary" to="/kontakt">
                                    {t('servicesPage.cardCta')}
                                </Link>
                            </div>
                        </div>
                        <div className="service-detail-hero__image-wrapper">
                            <img src={image} alt={t(`servicesPage.cards.${key}.title`)} className="service-detail-hero__image" />
                            <div className="service-detail-hero__icon-badge">
                                <Icon className="h-8 w-8" />
                            </div>
                        </div>
                    </div>
                </div>
            </section>

            <section className="section section--light">
                <div className="container">
                    <div className="service-detail-content">
                        <div className="service-detail-text">
                            <span className="section__eyebrow">Im Detail</span>
                            <h2>Darum geht es bei {t(`servicesPage.cards.${key}.title`)}</h2>
                            <p className="service-detail-text__body">
                                {t(`servicesPage.cards.${key}.detailsText`)}
                            </p>
                        </div>
                        <div className="service-detail-benefits">
                            <span className="section__eyebrow">Ihre Vorteile</span>
                            <h2>Warum wir der richtige Partner sind</h2>
                            <div className="benefits-grid">
                                {['one', 'two', 'three'].map((item) => (
                                    <article className="benefit-card" key={item}>
                                        <div className="benefit-card__icon-wrapper">
                                            <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="benefit-card__icon"><path d="M22 11.08V12a10 10 0 1 1-5.93-9.14"></path><polyline points="22 4 12 14.01 9 11.01"></polyline></svg>
                                        </div>
                                        <h3 className="benefit-card__title">
                                            {t(`servicesPage.cards.${key}.benefits.${item}.title`)}
                                        </h3>
                                        <p className="benefit-card__text">
                                            {t(`servicesPage.cards.${key}.benefits.${item}.text`)}
                                        </p>
                                    </article>
                                ))}
                            </div>
                        </div>
                    </div>
                </div>
            </section>

            <CallToAction
                title={t('servicesPage.cta.title')}
                description={t('servicesPage.cta.description')}
                ctaLabel={t('servicesPage.cta.button')}
                ctaTo="/kontakt"
            />
        </>
    );
};
